/**
 * 2.0 room, Part 8 #10: send when back online. The pure and storage half of the room's send queue
 * (`useRoomStream` owns the timing; this file owns what a queued message is, when it counts as waiting or as
 * not sent, which failures queue it, and where it is kept).
 *
 * README section 7:
 *   - A message queues only when `navigator.onLine` is false, or a send fails at the network layer before any
 *     response. Every other interruption keeps the room's interruption line.
 *   - It goes on reconnect, in order, one at a time.
 *   - After 10 minutes waiting (`--send-queue-timeout`) it is "Not sent. Tap to send." and goes only on a tap.
 *   - Each queued message carries a client idempotency key, made when it is first sent or queued and kept on
 *     every retry, so a retry whose first run the server finished or is still running never runs twice
 *     (server half: `api/lib/chat/room/roomIdempotency.js`).
 *   - The queue lives in memory and in local storage: it survives an app background, not a sign-out.
 *
 * Where it is kept: the thread cache (`lib/conversations.ts`) drops every message field it does not know on load
 * and is written to the server when online, so a queued message there would come back as a sent one with no
 * reply. The queue has its own local key beside it, per account, removed at sign-out.
 */

import { isCoachId, type CoachId } from './coaches';

/** README `--send-queue-timeout` (tokens.css): a message waits this long before it needs a tap. */
export const SEND_QUEUE_TIMEOUT_MS = 600_000;

/** The pause before the head of the queue is tried again after a failure that leaves it waiting; the last repeats. */
export const SEND_QUEUE_RETRY_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;

/** One message waiting to go (README State management: `{id, text, idempotencyKey, queuedAt}`, plus what a retry needs). */
export interface QueuedSend {
  id: string;
  text: string;
  /** 16 to 128 characters of [A-Za-z0-9_-]; made once, sent on every attempt. */
  idempotencyKey: string;
  /** When it first waited (ms since the epoch); the 10 minutes count from here. */
  queuedAt: number;
  /** The coach the athlete called for this message, if any (the one call that goes with it). */
  calledCoachId?: CoachId;
  /** The athlete's message is already in the thread (a send that failed after the thread showed it). */
  inThread?: boolean;
  /**
   * The account that wrote it (null: no account id was given, a memory-only queue). Every entry in a queue is its
   * owner's: a late failure of one account's send never joins another account's queue, and the send itself goes out
   * under the B234 `expectedUid` guard, so it is refused if the session is no longer the owner's.
   */
  owner: string | null;
  /**
   * The server has answered this key (its `done` arrived) but the thread holding the reply is not known to be saved.
   * The entry stays until the thread has been persisted, so an app closed during that save loses nothing: on the next
   * open the thread decides (it already holds the reply: the entry is dropped; it does not: the message goes again
   * under its key and the server replays the stored answer). It is never drawn as a waiting message.
   */
  answered?: boolean;
}

/** What the screen says about a queued message: `sending` is not waiting at all (it is drawn as a sent one). */
export type QueuedStatus = 'sending' | 'waiting' | 'not_sent';

const KEY_SHAPE = /^[A-Za-z0-9_-]{16,128}$/;

const randomHex = (): string => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** A fresh idempotency key for one message (the server accepts 16 to 128 characters of [A-Za-z0-9_-]). */
export function newIdempotencyKey(): string {
  return `room-${randomHex()}`;
}

/** A fresh id for one queue entry. */
export function newQueueId(): string {
  return `q-${randomHex()}`;
}

export function isIdempotencyKey(value: unknown): value is string {
  return typeof value === 'string' && KEY_SHAPE.test(value);
}

/**
 * True for a send that failed at the network layer before any response: `fetch` rejects with a TypeError
 * ("Failed to fetch", "Load failed", "NetworkError when attempting to fetch resource"). Our own timeout abort
 * (an AbortError) is not one, nor is anything that happened after a response head arrived.
 */
export function isNetworkLayerFailure(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  return !!err && typeof err === 'object' && (err as { name?: unknown }).name === 'TypeError';
}

/** The 409 body the server answers while the first run of the same key is still going. */
export function isInProgressBody(body: unknown): boolean {
  return !!body && typeof body === 'object' && (body as { status?: unknown }).status === 'in_progress';
}

/** How long to wait before the next try, after `failures` failed tries in a row (1 for the first). */
export function retryDelayMs(failures: number): number {
  const i = Math.min(Math.max(failures, 1), SEND_QUEUE_RETRY_MS.length) - 1;
  return SEND_QUEUE_RETRY_MS[i]!;
}

/** `sending` while it is the one in flight, `not_sent` once it has waited the limit, else `waiting`. */
export function queuedStatus(entry: QueuedSend, now: number, sendingId: string | null): QueuedStatus {
  if (sendingId !== null && entry.id === sendingId) return 'sending';
  return now - entry.queuedAt >= SEND_QUEUE_TIMEOUT_MS ? 'not_sent' : 'waiting';
}

/**
 * The status of every message in the queue, oldest first. As `queuedStatus`, with two more facts:
 *   - a message behind one that is not sent cannot go before it (they go in order), so it reads "not sent" too, the
 *     one line for the whole blocked run; any tap on it starts the run from the oldest (`released` is the set a tap
 *     has let go: they are waiting to go again, not blocked, and they do not block what is behind them);
 *   - a released message past its 10 minutes is waiting, not "not sent": the tap has been made.
 */
export function queueStatuses(
  entries: readonly QueuedSend[],
  now: number,
  sendingId: string | null,
  released: ReadonlySet<string>,
): QueuedStatus[] {
  let blocked = false;
  return entries.map((entry) => {
    if (sendingId !== null && entry.id === sendingId) return 'sending';
    // An answered message is a sent one, drawn as such (it is in the thread); it neither waits nor blocks what is behind it.
    if (entry.answered === true) return 'sending';
    if (released.has(entry.id)) return 'waiting';
    const timedOut = now - entry.queuedAt >= SEND_QUEUE_TIMEOUT_MS;
    if (blocked || timedOut) {
      blocked = true;
      return 'not_sent';
    }
    return 'waiting';
  });
}

/** What `authFetch` answers when the session is no longer the account `expectedUid` named (B234): nothing was sent. */
export function isAccountChangedAnswer(response: { status: number; statusText?: string }): boolean {
  return response.status === 409 && response.statusText === 'account changed mid-request';
}

// ── storage ───────────────────────────────────────────────────────────────────────────────────────

export const QUEUE_STORAGE_PREFIX = 'wai_sports_room_queue_';
export const queueStorageKey = (userId: string): string => `${QUEUE_STORAGE_PREFIX}${userId}`;

function sanitize(raw: unknown, userId: string): QueuedSend[] {
  if (!Array.isArray(raw)) return [];
  const out: QueuedSend[] = [];
  for (const item of raw) {
    const e = (item && typeof item === 'object' ? item : {}) as Partial<QueuedSend>;
    if (typeof e.id !== 'string' || e.id.length === 0) continue;
    if (typeof e.text !== 'string' || e.text.trim().length === 0) continue;
    if (!isIdempotencyKey(e.idempotencyKey)) continue;
    if (typeof e.queuedAt !== 'number' || !Number.isFinite(e.queuedAt)) continue;
    // An entry is its owner's: one stamped with another account is not read (a key holds one account's queue).
    if (e.owner !== undefined && e.owner !== userId) continue;
    out.push({
      id: e.id,
      text: e.text,
      idempotencyKey: e.idempotencyKey,
      queuedAt: e.queuedAt,
      owner: userId,
      ...(isCoachId(e.calledCoachId) ? { calledCoachId: e.calledCoachId } : {}),
      ...(e.inThread === true ? { inThread: true } : {}),
      ...(e.answered === true ? { answered: true } : {}),
    });
  }
  return out;
}

/**
 * Whether the thread already holds the answer to `text`: the athlete's last message is those words and an assistant
 * reply (not a system note) follows it. The words alone are not enough: a thread saved before the reply was has them
 * and no reply, and that turn is still owed.
 */
export function threadHoldsAnswer(
  messages: ReadonlyArray<{ role: string; content: string; system?: boolean }>,
  text: string,
): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.role === 'user' && m.system !== true) {
      if (m.content !== text) return false;
      return messages.slice(i + 1).some((r) => r.role === 'assistant' && r.system !== true && r.content.trim().length > 0);
    }
  }
  return false;
}

/** The account's stored queue, oldest first; an unreadable or foreign shape is an empty queue. */
export function readQueue(userId: string): QueuedSend[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = window.localStorage.getItem(queueStorageKey(userId));
    return stored ? sanitize(JSON.parse(stored), userId) : [];
  } catch {
    return [];
  }
}

/** Stores the account's queue (an empty queue removes the key). Best effort: quota and private mode never throw. */
export function writeQueue(userId: string, entries: readonly QueuedSend[]): void {
  if (typeof window === 'undefined') return;
  try {
    if (entries.length === 0) window.localStorage.removeItem(queueStorageKey(userId));
    else window.localStorage.setItem(queueStorageKey(userId), JSON.stringify(entries));
  } catch {
    // quota / private mode: the queue stays in memory
  }
}

/**
 * Counts the sign-outs this page has seen (`clearAllQueues` is the one place it moves). A room hook notes it when it
 * binds to an account; once it has moved the hook's session is over, and the hook writes no queue key again: a send
 * of the signed-out account that fails late (the room unmounts at sign-out without a render, and its fetch is not
 * aborted, as leaving the room must not cut a reply) must not write the account's words back after the sweep.
 */
let signOutEpoch = 0;
export const queueEpoch = (): number => signOutEpoch;

/** An account's queue is cleared by the hook when its account changes. */
export function clearQueue(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(queueStorageKey(userId));
  } catch {
    // nothing to clear
  }
}

/**
 * Sign-out (`AuthContext`'s SIGNED_OUT branch, beside the other per-account purges): every account's queue key on this
 * device goes, by the prefix, because the signed-out screen unmounts the room before any hook could see the account
 * go and a shared phone must hold no one's waiting words. Never throws.
 */
export function clearAllQueues(): void {
  // Every room hook that is alive or in flight now belongs to a session that has ended: see `queueEpoch`.
  signOutEpoch += 1;
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(QUEUE_STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // blocked storage holds no queue to leave behind
  }
}

/**
 * A session arrived (`AuthContext`, beside `purgeForeignOnboardingState`): every room queue key on the device that is
 * not this account's goes. A sign-out sweeps them all, but a send of the account that left can still fail late and write
 * its key back after the sweep (the guard on that is `queueEpoch`); the next session of someone else removes whatever
 * got through. The account's own key is left alone (its waiting messages are its own, and are sent). Never throws.
 */
export function clearQueuesExcept(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const own = queueStorageKey(userId);
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(QUEUE_STORAGE_PREFIX) && key !== own) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // blocked storage holds no queue to leave behind
  }
}
